// Structure the DOM gives you via <fieldset> nesting, put back onto the flat
// HTMLFormControlsCollection API. A form's `elements` is a single flat list; this
// wraps a form (or a fieldset) as a TREE whose groups are its child fieldsets:
//
//   import { toTree } from "../lib/html_form_controls_tree"
//   const tree = toTree(form)            // or toTree(form.elements), or toTree(fieldset)
//
//   tree.controls        // direct-child controls (NOT inside a nested fieldset)
//   tree.groups          // direct-child <fieldset> elements
//   tree.flatControls    // every control at/below this node, any depth
//   tree.flatGroups      // every <fieldset> at/below this node, any depth
//
//   toTree(tree.groups[0]).controls   // recurse: a group's own direct controls
//
// The control views (.controls / .flatControls) are NamedLists — array-backed, so
// they iterate/map/filter, but string keys also resolve a control by name or id,
// matching how `form.elements` supports BOTH elements[0] and elements['int']:
//
//   tree.controls['int']            // by name
//   tree.controls.namedItem('int')  // the collection-idiom method
//   tree.controls[0]                // by index
//   [...tree.controls].map(...)     // real Array methods
//
// Name resolution mirrors HTMLFormControlsCollection faithfully, including the
// same-name case: a name matching ONE control returns that element; a name
// matching MANY returns a RadioNodeList-like view (indexed/iterable + a `.value`
// that reads/writes the checked radio). So a scoped view never silently downgrades
// the access contract the native collection gives you.
//
// The group views (.groups / .flatGroups) return the raw <fieldset> elements —
// HTMLFormControlsCollection can't be constructed and can't hold fieldsets anyway,
// so there's no faithful "collection of groups" to return; re-wrap one with
// toTree() to descend.
//
// Why not subclass HTMLFormControlsCollection / patch its prototype: it has no
// public constructor (`new` throws "Illegal constructor"), and patching host DOM
// prototypes is a cross-library footgun. A plain factory keeps it honest.

// A fieldset is itself a listed element, so form.elements / fieldset.elements
// include <fieldset> members. Control views exclude them.
const isFieldset = (el) => el.tagName === "FIELDSET"

// A RadioNodeList-like view over same-name controls, mirroring what the native
// collection returns when a name matches more than one control. It's an
// array-Proxy (index / iterate / length / map,filter) plus a `.value` accessor
// with the native RadioNodeList semantics:
//   get -> the checked radio's value; "" if none checked or no radios present
//   set -> check the radio whose value matches (native also does this)
function radioNodeList(elements) {
  return new Proxy(elements, {
    get(target, prop, receiver) {
      if (prop === "value") {
        return target.find((e) => e.type === "radio" && e.checked)?.value ?? ""
      }
      return Reflect.get(target, prop, receiver)
    },
    set(target, prop, val, receiver) {
      if (prop === "value") {
        for (const e of target) if (e.type === "radio") e.checked = e.value === val
        return true
      }
      return Reflect.set(target, prop, val, receiver)
    },
  })
}

// Resolve a name/id key the way HTMLFormControlsCollection.namedItem does:
//   no match  -> null
//   one match -> that element
//   many      -> a RadioNodeList-like list (see radioNodeList)
function resolveName(elements, key) {
  const hits = elements.filter((e) => e.name === key || e.id === key)
  if (hits.length === 0) return null
  if (hits.length === 1) return hits[0]
  return radioNodeList(hits)
}

// Array-backed collection with form.elements-style dual access: numeric index OR
// control name/id string key, plus namedItem(). Name resolution matches the native
// collection exactly, INCLUDING the same-name -> RadioNodeList case. The name
// lookup is skipped when the key is a real array member (length, map, indices...)
// so a control named "map" can't shadow Array.prototype — reach it via
// namedItem('map').
function namedList(elements) {
  return new Proxy(elements, {
    get(target, prop, receiver) {
      if (prop === "namedItem") return (k) => resolveName(target, k)
      if (typeof prop === "string" && !(prop in target) && !/^\d+$/.test(prop)) {
        const hit = resolveName(target, prop)
        if (hit) return hit
      }
      return Reflect.get(target, prop, receiver)
    },
    has(target, prop) {
      if (typeof prop === "string" && resolveName(target, prop)) return true
      return Reflect.has(target, prop)
    },
  })
}

export class HTMLFormControlsTree {
  // element: the <form> or <fieldset> this node wraps.
  constructor(element) {
    if (!element || !element.elements) {
      throw new TypeError("HTMLFormControlsTree expects a <form> or <fieldset>")
    }
    this.element = element
  }

  // The fieldset that is THIS node's boundary: its own element when it wraps a
  // fieldset, or null for a form root (a form is not a fieldset).
  get #boundary() {
    return isFieldset(this.element) ? this.element : null
  }

  // The nearest <fieldset> strictly ABOVE el (never el itself — start from the
  // parent). Uniform for controls and fieldset members alike.
  #nearestGroupAbove(el) {
    return el.parentElement?.closest("fieldset") ?? null
  }

  // An element is a DIRECT child of this node when the nearest fieldset above it is
  // this node's boundary — i.e. no deeper fieldset sits between them.
  #isDirect(el) {
    return this.#nearestGroupAbove(el) === this.#boundary
  }

  // Direct-child controls (excludes fieldsets and anything inside a nested one).
  get controls() {
    return namedList([...this.element.elements].filter((el) => !isFieldset(el) && this.#isDirect(el)))
  }

  // Direct-child <fieldset> elements (not fieldsets nested inside another group).
  get groups() {
    return [...this.element.elements].filter((el) => isFieldset(el) && this.#isDirect(el))
  }

  // Every control at/below this node, any depth. element.elements is already deep;
  // just drop the fieldset members.
  get flatControls() {
    return namedList([...this.element.elements].filter((el) => !isFieldset(el)))
  }

  // Every <fieldset> at/below this node, any depth.
  get flatGroups() {
    return [...this.element.elements].filter(isFieldset)
  }
}

// Idiomatic to* factory. Accepts a <form>/<fieldset>, or an HTMLFormControls
// collection (uses its owning form).
export function toTree(source) {
  if (source instanceof HTMLFormControlsTree) return source
  if (source && source.elements) return new HTMLFormControlsTree(source)
  // An HTMLFormControlsCollection — root at the form that owns it.
  const owner = source && source.length ? source[0]?.form : null
  if (owner) return new HTMLFormControlsTree(owner)
  throw new TypeError("toTree expects a <form>, <fieldset>, or a non-empty form controls collection")
}
